import { useState } from "react";
import { useIsFetching, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { adminDelete, adminPatch, adminPost, useForexAdmin, useOperator } from "./api";
import { Drawer, Panel, QueryState, inputCls, labelCls, mono, muted } from "./parts";
import { useRoles } from "./Roles";

const errBox = "rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300";

// 025: the id from the admin's dashboard token. The backend does not check it
// with the dashboard, so a mistyped id is granted: check its shape at least.
const OPERATOR_ID = /^[0-9a-f]{24}$/;

export const useOperators = () => useForexAdmin("/operators/admin");

// Grant (operator null) or change one operator. Grant answers 200, not 201,
// and granting someone already granted renames them and gives them the role.
// No optimistic UI: the lockout guard's 409 changes nothing.
function OperatorDrawer({ operator, onClose, roles }) {
  const qc = useQueryClient();
  const [operatorId, setOperatorId] = useState("");
  const [name, setName] = useState(operator?.name ?? "");
  const [roleId, setRoleId] = useState(operator?.role.id ?? "");
  const m = useMutation({
    mutationFn: () => {
      if (!operator) return adminPost("/operators/admin", { operatorId: operatorId.trim(), name: name.trim(), roleId });
      // Send only what changed; PATCH with neither is a 400.
      const body = {};
      if (name.trim() !== operator.name) body.name = name.trim();
      if (roleId !== operator.role.id) body.roleId = roleId;
      return adminPatch(`/operators/admin/${operator.operatorId}`, body);
    },
    onSuccess: () => {
      // Also refetches /me, for when the caller changed their own role.
      qc.invalidateQueries({ queryKey: ["forex-admin"] });
      onClose();
    },
  });
  const idOk = operator || OPERATOR_ID.test(operatorId.trim());
  const changed = !operator || name.trim() !== operator.name || roleId !== operator.role.id;
  const ready = idOk && name.trim() && roleId && changed;

  return (
    <Drawer open onClose={onClose} title={operator ? `Change ${operator.name}` : "Grant forex access"}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        {operator ? (
          <p className="font-mono text-xs text-muted-foreground">{operator.operatorId}</p>
        ) : (
          <div>
            <label className={labelCls}>Dashboard admin id</label>
            <input
              value={operatorId}
              onChange={(e) => setOperatorId(e.target.value)}
              placeholder="24 lower case hex characters"
              className={`${inputCls} font-mono`}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              The id in the admin's dashboard token. It is not checked with the dashboard, so a mistyped id is granted.
            </p>
            {operatorId && !idOk && <p className="mt-1 text-xs text-red-400">Must be 24 characters of 0-9 and a-f</p>}
          </div>
        )}
        <div>
          <label className={labelCls}>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Role</label>
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)} className={inputCls}>
            <option value="">Choose…</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        {m.error && <p className={errBox}>{m.error.message}</p>}
        <Button size="sm" type="submit" disabled={!ready || m.isPending}>
          {m.isPending ? "Saving…" : operator ? "Save" : "Grant access"}
        </Button>
      </form>
    </Drawer>
  );
}

function RevokeOperator({ operator, onClose }) {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ["forex-admin"] });
    onClose();
  };
  const m = useMutation({
    mutationFn: () => adminDelete(`/operators/admin/${operator.operatorId}`),
    onSuccess: done,
    // A repeated revoke is a 404: it went through (025).
    onError: (err) => err.status === 404 && done(),
  });
  return (
    <Drawer open={!!operator} onClose={() => { m.reset(); onClose(); }} title={`Revoke ${operator?.name ?? "operator"}`}>
      <p className="text-sm text-muted-foreground">
        They lose Forex Admin from their very next request. Granting them again later starts a new grant.
      </p>
      {m.error && <p className={errBox}>{m.error.message}</p>}
      <Button size="sm" variant="destructive" disabled={m.isPending} onClick={() => m.mutate()}>
        {m.isPending ? "Revoking…" : "Revoke access"}
      </Button>
    </Drawer>
  );
}

const COLUMNS = [{ label: "Name" }, { label: "Operator id" }, { label: "Role" }, { label: "Granted" }, { label: "" }];

export default function Operators() {
  const me = useOperator().data?.operatorId;
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;
  const operators = useOperators();
  const roles = useRoles();
  // editing: null = closed, "new" = grant, an operator = change them.
  const [editing, setEditing] = useState(null);
  const [revoking, setRevoking] = useState(null);

  return (
    <QueueLayout
      eyebrow="Forex Admin · Access"
      title="Operators"
      description="Dashboard admins granted Forex Admin, each with one role. A change bites on their next request. The last operator holding both roles.manage and operators.manage cannot be revoked or given another role."
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      {roles.data && <Button size="sm" onClick={() => setEditing("new")}>+ Grant access</Button>}
      <Panel title="Operators">
        <QueryState q={operators}>
          {(rows) => (
            <QueueTable
              columns={COLUMNS}
              rows={rows}
              renderRow={(o) => (
                <tr key={o.operatorId}>
                  <Td className="whitespace-nowrap">
                    {o.name}
                    {o.operatorId === me && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                  </Td>
                  <Td className={mono}>{o.operatorId}</Td>
                  <Td className="whitespace-nowrap">
                    {o.role.name}
                    <div className="font-mono text-[10px] text-muted-foreground">{o.role.id}</div>
                  </Td>
                  <Td className={muted}>{formatDateTime(o.createdAt)}</Td>
                  <Td className="whitespace-nowrap text-xs">
                    {roles.data && (
                      <Button size="sm" variant="outline" onClick={() => setEditing(o)}>Change</Button>
                    )}
                    <Button size="sm" variant="destructive" className="ml-2" onClick={() => setRevoking(o)}>
                      Revoke
                    </Button>
                  </Td>
                </tr>
              )}
            />
          )}
        </QueryState>
      </Panel>
      {editing && (
        <OperatorDrawer
          key={editing === "new" ? "new" : editing.operatorId}
          operator={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          roles={roles.data}
        />
      )}
      {/* Keyed, so each opening starts without the last one's error. */}
      <RevokeOperator key={revoking?.operatorId} operator={revoking} onClose={() => setRevoking(null)} />
    </QueueLayout>
  );
}
